/**
 * Salaisuuksien jako (docs/SALAISUUDET.md).
 *
 * Säännöt, jotka selain ja palvelin jakavat: rajat, PIN-koodin muoto,
 * luonnin skeema ja vastausten tyypit. Sama koodi molemmissa siksi, että
 * lomake torjuu saman PIN:n, jonka palvelin torjuisi — muuten käyttäjä
 * näkisi virheen vasta lähettäessään, ja silloin sisältökenttä on jo
 * täytetty salasanalla, jota hän ei halua kirjoittaa uudelleen.
 */
import { z } from 'zod';
/** Elinkaaren tilat (luku 4). Vastaa kannan `SharedSecretState`-enumia. */
export const SECRET_STATES = ['PENDING', 'READ', 'EXPIRED', 'REVOKED', 'DESTROYED'];
export const SECRET_DEFAULT_TTL_DAYS = 7;
export const SECRET_MIN_TTL_MINUTES = 15;
export const SECRET_MAX_TTL_DAYS = 30;
/** Merkkeinä. Riittää salasanalle, avaimelle, varmenteelle ja .env-tiedostolle. */
export const SECRET_MAX_LENGTH = 10_000;
export const SECRET_NAME_MAX_LENGTH = 120;
export const SECRET_PIN_MIN_LENGTH = 4;
export const SECRET_PIN_MAX_LENGTH = 6;
/** Viides väärä yritys tuhoaa salaisuuden (luku 7). */
export const SECRET_PIN_MAX_ATTEMPTS = 5;
/**
 * Lopputilan metatiedot (IP, selain) poistetaan tämän jälkeen (luku 13).
 * Vakio eikä asetus: arvo on kirjattu käsittelyselosteeseen.
 */
export const SECRET_METADATA_RETENTION_DAYS = 90;
/**
 * Linkin avaimen etuliite. Tekee avaimesta tunnistettavan lokista ja
 * salaisuusskannereille, ja autentikointikoukku tunnistaa siitä, ettei
 * Bearer-arvo ole API-tunnus.
 */
export const SECRET_KEY_PREFIX = 'ds_';
/** Vanhenemisen pikavalinnat minuutteina. Oletus on 7 päivää. */
export const SECRET_TTL_PRESETS = [
    { key: '1h', minutes: 60 },
    { key: '1d', minutes: 24 * 60 },
    { key: '3d', minutes: 3 * 24 * 60 },
    { key: '7d', minutes: 7 * 24 * 60 },
    { key: '14d', minutes: 14 * 24 * 60 },
    { key: '30d', minutes: 30 * 24 * 60 },
];
/**
 * Onko PIN triviaali: kaikki samaa numeroa tai nouseva/laskeva sarja.
 *
 * Ihmisen keksimä PIN on muuten todennäköisesti `1234`, ja viiden yrityksen
 * raja ei suojaa mitään, jos ensimmäinen arvaus osuu.
 */
export function isTrivialPin(pin) {
    if (!/^\d+$/.test(pin) || pin.length < 2)
        return false;
    const numerot = [...pin].map(Number);
    const samat = numerot.every((n) => n === numerot[0]);
    const nousee = numerot.every((n, i) => i === 0 || n === (numerot[i - 1] + 1) % 10);
    const laskee = numerot.every((n, i) => i === 0 || n === (numerot[i - 1] + 9) % 10);
    return samat || nousee || laskee;
}
/** Kelpaako PIN jakajan omaksi: 4–6 numeroa eikä triviaali. */
export function pinOngelma(pin) {
    const muoto = new RegExp(`^\\d{${SECRET_PIN_MIN_LENGTH},${SECRET_PIN_MAX_LENGTH}}$`);
    if (!muoto.test(pin))
        return 'muoto';
    if (isTrivialPin(pin))
        return 'triviaali';
    return null;
}
export const secretPinSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('generate') }),
    z.object({
        mode: z.literal('custom'),
        value: z.string().superRefine((arvo, ctx) => {
            const ongelma = pinOngelma(arvo);
            if (ongelma === 'muoto') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `PIN-koodissa on ${SECRET_PIN_MIN_LENGTH}–${SECRET_PIN_MAX_LENGTH} numeroa.`,
                });
            }
            else if (ongelma === 'triviaali') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'PIN-koodi on liian helppo arvata. Anna toinen tai anna järjestelmän arpoa se.',
                });
            }
        }),
    }),
]);
/**
 * Salaisuuden luonti.
 *
 * Sisältöä ei trimmata: välilyönti voi olla osa salasanaa. Virheviestit
 * eivät koskaan toista syötettä — sisältökentän viesti päätyisi muuten
 * vastaukseen ja mahdollisesti lokiin.
 */
export const secretCreateSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, { message: 'Anna salaisuudelle nimi.' })
        .max(SECRET_NAME_MAX_LENGTH, {
        message: `Nimi voi olla enintään ${SECRET_NAME_MAX_LENGTH} merkkiä.`,
    }),
    content: z
        .string()
        .max(SECRET_MAX_LENGTH, {
        message: `Salaisuus voi olla enintään ${SECRET_MAX_LENGTH.toLocaleString('fi-FI')} merkkiä.`,
    })
        .refine((arvo) => arvo.trim().length > 0, { message: 'Salaisuus ei voi olla tyhjä.' }),
    expiresAt: z.string().datetime({ offset: true, message: 'Anna vanhenemisaika.' }),
    pin: secretPinSchema.optional(),
});
export const secretListQuerySchema = z.object({
    scope: z.enum(['own', 'all']).default('own'),
    state: z.enum(SECRET_STATES).optional(),
});
/**
 * Lukupyynnön runko. Tyhjä runko on sallittu — PIN:ttömän salaisuuden
 * lukija ei lähetä mitään. PIN:n muotoa ei tarkisteta tässä: väärän
 * muotoinen PIN on väärä PIN ja kuluttaa yrityksen kuten muutkin.
 */
export const secretRevealSchema = z
    .object({ pin: z.string().max(32).optional() })
    .optional()
    .transform((arvo) => arvo ?? {});
//# sourceMappingURL=salaisuudet.js.map